import { Button } from "components/DesignSystem/Button/Button";
import { FileIcon } from "components/design/FileIcon";
import { Spinner } from "components/DesignSystem/Loader/loader";
import { Trash20 } from "components/icons/Trash20";
import { Preview } from "components/PreviewModal";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { KeyboardEvent, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useParams } from "react-router-dom";
import { BackendError } from "types/utils/error";
import { fileTypeFromName } from "util/fileTypeFromName";
import {
  DocumentFile,
  EntitySection,
  useDeleteDocumentMutation,
  useReplaceDocumentMutation,
} from "v2/store/entityDocumentsApi";
import { DeleteConfirm } from "./DeleteConfirm";

type FileRowProps = {
  file: DocumentFile;
  section: string | null;

  entityId?: string;
  onReplaceClick?: () => void;
  onDeleteClick?: () => void;
};

export const FileRow = ({
  file,
  section,
  entityId,
  onReplaceClick,
  onDeleteClick,
}: FileRowProps) => {
  const { entityId: routeEntityId = "" } = useParams<{ entityId: string }>();
  const documentEntityId = entityId ?? routeEntityId;
  const { successToast, alertToast } = useToast();
  const deleteModal = useModal();
  const [showPreview, setShowPreview] = useState(false);

  const [replaceDocument, { isLoading: isReplacing }] =
    useReplaceDocumentMutation();
  const [deleteDocument, { isLoading: isDeleting }] =
    useDeleteDocumentMutation();

  const handleReplace = async (accepted: File[]): Promise<void> => {
    if (accepted.length === 0) return;

    try {
      await replaceDocument({
        entityId: documentEntityId,
        fileUuid: file.uuid,
        file: accepted[0],
      }).unwrap();
      successToast({ message: "Document updated successfully" });
    } catch (e) {
      alertToast({ message: (e as BackendError)?.data?.error?.message });
    }
  };

  const { getInputProps, open } = useDropzone({
    onDrop: (accepted) => void handleReplace(accepted),
    multiple: false,
    noDrag: true,
    noClick: true,
  });

  const handleConfirmDelete = async (): Promise<void> => {
    try {
      await deleteDocument({
        entityId: documentEntityId,
        fileUuid: file.uuid,
      }).unwrap();
      successToast({ message: "Document deleted successfully" });
      deleteModal.close();
    } catch (e) {
      alertToast({ message: (e as BackendError)?.data?.error?.message });
    }
  };

  const iconType = file.fileType || fileTypeFromName(file.name);

  const handleReplaceClick = (): void => {
    onReplaceClick?.();
    open();
  };

  const handleDeleteClick = (): void => {
    onDeleteClick?.();
    deleteModal.open();
  };

  const handleViewClick = (): void => setShowPreview(true);
  const handleClosePreview = (): void => setShowPreview(false);
  const onConfirmDelete = (): void => void handleConfirmDelete();

  const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>): void => {
    if (event.key === "Enter" || event.key === " ") handleViewClick();
  };

  return (
    <div className="t-flex t-items-center t-gap-2">
      {isReplacing ? (
        <div className="t-flex t-min-w-0 t-flex-1 t-items-center t-gap-1.5 t-text-body-sm t-italic t-text-text-30">
          <Spinner size="small" />
          Uploading document...
        </div>
      ) : (
        <div className="t-flex t-min-w-0 t-flex-1 t-items-center">
          <span
            role="button"
            tabIndex={0}
            onClick={handleViewClick}
            onKeyDown={handleKeyDown}
            className="t-flex t-min-w-0 t-cursor-pointer t-items-center t-gap-1 t-text-body-sm t-text-text-30 hover:t-text-text-100"
          >
            <span className="t-flex t-shrink-0">
              <FileIcon fileType={iconType} size="20" />
            </span>
            <span className="t-truncate">{file.name}</span>
          </span>
        </div>
      )}

      <input {...getInputProps()} />

      {!isReplacing && (
        <div className="t-flex t-shrink-0 t-items-center t-gap-2">
          <Button
            type="button"
            size="small"
            customType="ghost"
            onClick={handleReplaceClick}
          >
            Replace
          </Button>
          <Button
            type="button"
            size="small"
            customType="ghost"
            onClick={handleDeleteClick}
            aria-label="Delete document"
          >
            <Trash20 className="t-size-4" />
          </Button>
        </div>
      )}

      <DeleteConfirm
        {...deleteModal}
        isLoading={isDeleting}
        onConfirm={onConfirmDelete}
        description={
          <div className="t-text-body t-text-text-30">
            You're about to delete {file.name}
            {section ? (
              <>
                , which is currently matched to{" "}
                <span className="t-font-semibold t-text-text-100">
                  {section}
                </span>
              </>
            ) : null}
            .
          </div>
        }
      />

      <Preview
        showModal={showPreview}
        closeModal={handleClosePreview}
        previewId={showPreview ? file.uuid : null}
      />
    </div>
  );
};
